import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../App";
import { useTranslation } from "react-i18next";
import { ConvertMmToPx, formatDate } from "../../../hooks/Method";
import Barcode from "react-barcode/lib/react-barcode";

const ThermalPrinterRecept = ({ data }) => {
  const { settings } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...data.store });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: `${
          store.receiptWidth ? ConvertMmToPx(store.receiptWidth) : "220"
        }px`,
        width: "100%",
        fontFamily: "monospace",
        fontSize: 10,
        fontWeight: 600,
        margin: 0,
        padding: 0,
        color: "#000000",
        lineHeight: "1.2",
        whiteSpace: "pre-wrap",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "4px",
          paddingBottom: "4px",
          borderBottom: "2px dashed #333",
        }}
      >
        {store.logo && store.showLogoOnReceipt === true && (
          <div
            style={{
              marginBottom: "5px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logo}
              alt="store_logo"
              style={{
                maxHeight: "100px",
                maxWidth: "150px",
                objectFit: "contain",
              }}
            />
          </div>
        )}
        {store.showStoreNameOnReceipt && (
          <>
            <div style={{ fontSize: "14px" }}>{store.storeName}</div>
            {store.localName && (
              <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                {store.localName}
              </div>
            )}
          </>
        )}
        {store.showTaglineOnReceipt && (
          <div
            style={{
              display: "grid",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontStyle: "italic",
                letterSpacing: "0.5px",
                padding: "0px 5px",
                border: "2px dashed #000000",
                textAlign: "center",
                width: "fit-content",
                marginBottom: "5px",
              }}
            >
              <span>{store.tagline}</span>
            </div>
          </div>
        )}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div>
            {store.showAddressOnReceipt && (
              <>
                {store.address && (
                  <span
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {store.address}
                  </span>
                )}
                <div>
                  {store.city ? `, ${store.city}` : ""}
                  {store.state ? `, ${store.state}` : ""}
                  {store.zip ? `, ${store.zip}` : ""}
                </div>
              </>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "5px",
              }}
            >
              {store.showPhoneOnReceipt && store.phone && (
                <span>{store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>{store.email}</span>
              )}
              {store.website && <span>{store.website}</span>}
              {store.socialMedia && (
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {store.socialMedia}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gap: "2px",
          marginBottom: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "5px" }}>
          <span>{t("invoiceNo")}:</span>
          <span>#{data.id}</span>
        </div>
        <div style={{ display: "flex", gap: "5px" }}>
          <span>{t("date")}&nbsp;:</span>
          <span>{formatDate(data.sellDate)}</span>
        </div>
        <div style={{ display: "flex", gap: "5px" }}>
          <span>{t("customer")}:</span>
          <span>{data.cstmr ? data.cstmr.fullName : t("walkInCustomer")}</span>
        </div>

        {data.cstmr && (
          <>
            <div style={{ display: "flex", gap: "5px" }}>
              <span>{t("phone")}:</span>
              <span>{data.cstmr.phone}</span>
            </div>
          </>
        )}
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "5px",
          borderBottom: "2px dashed #000000",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "4fr 1fr 2fr",
            gap: "2px",
            borderTop: "1px dashed #000000",
            borderBottom: "1px dashed #000000",
            marginBottom: "5px",
          }}
        >
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("totalPrice")}</div>
        </div>

        {data.sellItems?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "4fr 1fr 2fr",
              gap: "1px",
              marginBottom: "6px",
            }}
          >
            <div style={{}}>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.qty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.qty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gap: "6px",
          marginBottom: "5px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{t("subtotal")}:</span>
          <span>
            {settings.curSymbol} {data.subtotal.toFixed(settings.decimal)}
          </span>
        </div>

        {data.tax > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>
              {t("tax")} ({data.taxPct}%):
            </span>
            <span>
              +{settings.curSymbol} {data.tax.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        {data.disAmt > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>
              {t("discount")} {data.discountType === 1 && `(${data.discount}%)`}
              :
            </span>
            <span>
              (-) {settings.curSymbol} {data.disAmt.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "600",
            paddingTop: "2px",
            borderTop: "1px dashed #000000",
          }}
        >
          <span>{t("totalPay")}:</span>
          <span>
            {settings.curSymbol} {data.totalPay.toFixed(settings.decimal)}
          </span>
        </div>

        {data.payments.map((pay, ind) => (
          <div
            key={ind}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <span>{pay.payMethod?.title}:</span>
            <span>
              {settings.curSymbol} {pay.amount.toFixed(settings.decimal)}
            </span>
          </div>
        ))}

        {data.received > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("totalReceived")}:</span>
            <span>
              {settings.curSymbol} {data.received.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        {data.dueAmt > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: "#00000",
            }}
          >
            <span>{t("due")}:</span>
            <span>
              {settings.curSymbol} {data.dueAmt.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        {data.creditAmt > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: "#00000",
            }}
          >
            <span>{t("useCredit")}:</span>
            <span>
              {settings.curSymbol} {data.creditAmt.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "600",
            paddingTop: "2px",
            borderTop: "1px dashed #000000",
          }}
        >
          <span>{data.changeAddedCredit ? t("credit") : t("change")}:</span>
          <span>
            {settings.curSymbol} {data.change.toFixed(settings.decimal)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "grid",
          lineHeight: 1.3,
          borderTop: "2px dashed #000000",
          paddingTop: "5px",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          maxWidth: "100%",
        }}
      >
        {data.note && (
          <span
            style={{
              borderBottom: "1px dashed #000000",
              paddingBottom: "7px",
              marginBottom: "7px",
            }}
          >
            {data.note}
          </span>
        )}
        <span> {settings.salesTermForReceipt}</span>
      </div>
      <div
        style={{
          display: "grid",
          justifyContent: "center",
          alignItems: "center",
          textTransform: "uppercase",
          fontSize: "18px",
          borderBottom: "2px dashed #000000",
          borderTop: "2px dashed #000000",
          padding: "8px 0px",
          marginTop: "5px",
          letterSpacing: "2px",
        }}
      >
        ✦ {t("thankYou")} ✦
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        {data.id && (
          <Barcode
            value={data.id.toString()}
            displayValue={true}
            height={30}
            width={2}
            fontSize={14}
          />
        )}
      </div>
    </div>
  ) : null;
};

export default ThermalPrinterRecept;
